import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DeleteModalComponent } from 'app/components/modal/deleteModal.component';
import { AuthService } from 'app/services/auth.service';
import { DeviceRepository } from 'app/services/database/device.repository';
import { RoleRepository } from 'app/services/database/role.repository';
import { UserRepository } from 'app/services/database/user.repository';
import { ModalService } from 'app/services/modal.service';
import { PresentationService } from 'app/services/presentation/presentation.service';
import { Logger } from 'app/shared/logger';
import { LoggerFactory } from 'app/shared/loggerFactory';
import { EditScheduleEntryModalComponent } from 'app/views/devices/editScheduleEntryModal/editScheduleEntryModal.component';
import { DeviceModel } from 'app/views/devices/models/device.model';
import { ScheduleEntryModel } from 'app/views/devices/models/scheduleEntry.model';
import { PresentationModel } from 'app/views/presentations/models/presentation.model';
import { VideoModel } from 'app/views/presentations/models/video.model';
import * as moment from 'moment';

@Component({
    selector: 'psm-edit-schedule-entries',
    templateUrl: 'editScheduleEntries.component.html',
    styleUrls: ['editScheduleEntries.component.scss']
})

export class EditScheduleEntriesComponent implements OnInit {

    private logger: Logger;
    public isLoading: boolean;

    @Input()
    public schedule: ScheduleEntryModel[];

    @Output()
    public scheduleChange: EventEmitter<ScheduleEntryModel[]> = new EventEmitter();

    public scheduleEntriesPresentations: PresentationScheduleEntryPair[] = [];

    constructor(
        private authService: AuthService,
        private deviceRepository: DeviceRepository,
        private userRepository: UserRepository,
        private roleRepository: RoleRepository,
        private modalService: ModalService,
        private loggerFactory: LoggerFactory,
        private presentationService: PresentationService
    ) {
        this.logger = loggerFactory.create('EditScheduleEntriesComponent');
    }

    public async ngOnInit() {
        this.isLoading = true;
        let user = await this.authService.getCurrentUser();

        if (this.schedule) {
            for (let entry of this.schedule) {
                try {
                    let relatedPresentation = await this.presentationService.getPresentation(entry.presentationId);
                    await this.setThumbnailUrl(relatedPresentation);
                    this.scheduleEntriesPresentations.push(new PresentationScheduleEntryPair(relatedPresentation, entry));
                } catch (err) {
                    this.logger.error('An error occured while getting the user devices', err);
                }
            }
            this.sortScheduleEntries();
            this.highlightOverlapingEntries();
        } else {
            this.schedule = [];
        }
        this.isLoading = false;
    }

    public async openEditModal(editedSchedPres: PresentationScheduleEntryPair) {
        let editModalResponse: ScheduleEntryModel = await this.modalService.open<EditScheduleEntryModalComponent, ScheduleEntryModel>(EditScheduleEntryModalComponent, { inputScheduleEntry: editedSchedPres.scheduleEntry });
        this.isLoading = true;
        if (editModalResponse) {
            // Load presentation if changed or new
            if (editModalResponse.presentationId && editModalResponse.presentationId !== editedSchedPres.scheduleEntry.presentationId) {
                let relatedPresentation = await this.presentationService.getPresentation(editModalResponse.presentationId);
                await this.setThumbnailUrl(relatedPresentation);
                editedSchedPres.presentation = relatedPresentation;
            }
            if (editedSchedPres.scheduleEntry.presentationId) {
                // Not a new scheduleEntry
                editedSchedPres.scheduleEntry = editModalResponse;
            } else {
                // New scheduleEntry
                editedSchedPres.scheduleEntry = editModalResponse;
                this.scheduleEntriesPresentations.push(editedSchedPres);
            }
            this.sortScheduleEntries();
            this.applyToDevice();
            this.highlightOverlapingEntries();
        }
        this.isLoading = false;
    }

    public async openNewModal() {
        // new schedule
        await this.openEditModal(new PresentationScheduleEntryPair(undefined, new ScheduleEntryModel('',
            moment().startOf('day'),
            moment(moment().add(1, 'day')).startOf('day')
        )));
    }

    public async openDeleteModal(scheduleEntryPresentation: PresentationScheduleEntryPair) {
        let deleteModalResponse = await this.modalService.open(DeleteModalComponent, {
            title: 'modules.devices.edit.schedule.delete-modal-message'
        });
        if (deleteModalResponse) {
            this.scheduleEntriesPresentations = this.scheduleEntriesPresentations.filter(obj => JSON.stringify(obj.scheduleEntry) !== JSON.stringify(scheduleEntryPresentation.scheduleEntry));
            this.applyToDevice();
            this.highlightOverlapingEntries();
        }
    }

    private applyToDevice() {
        this.schedule = [];
        for (let schedule of this.scheduleEntriesPresentations) {
            this.schedule.push(schedule.scheduleEntry);
        }
        this.scheduleChange.emit(this.schedule);
    }

    private sortScheduleEntries() {
        this.scheduleEntriesPresentations.sort((a: PresentationScheduleEntryPair, b: PresentationScheduleEntryPair) => {
            return a.scheduleEntry.startDate - b.scheduleEntry.startDate;
        });
    }

    public highlightOverlapingEntries() {
        let overlapingList = this.presentationService.getOverlapingScheduleEntriesList(this.schedule);
        // Set overlaping entry in view list
        for (let schEntrPres of this.scheduleEntriesPresentations) {
            schEntrPres.isOverlaping = false; // Set back to false in case of fixing change
            for (let schEntrOver of overlapingList) {
                // Check if entry match overlaping list entry
                if (schEntrPres.scheduleEntry === schEntrOver) {
                    // Entry is in overlaping
                    schEntrPres.isOverlaping = true;
                }
            }
        }
    }

    private async setThumbnailUrl(presentation: PresentationModel) {
        presentation.thumbnailPath = await this.presentationService.getThumbnailUrl(presentation);
    }

    public shouldDisplayTime(entry: ScheduleEntryModel) {
        const startDate = new Date(entry.startDate);
        const endDate = new Date(entry.endDate);

        return startDate.getHours() === 0 && startDate.getMinutes() === 0 && endDate.getHours() === 0 && endDate.getMinutes() === 0;
    }

}

export class PresentationScheduleEntryPair {
    public isOverlaping: boolean = false;
    constructor(public presentation: PresentationModel, public scheduleEntry: ScheduleEntryModel) {
    }
}


// WEBPACK FOOTER //
// ./src/app/views/devices/editScheduleEntries/editScheduleEntries.component.ts